/**
 * Single-ball motion over one fixed timestep, following the Petit/Coriolis cloth
 * model: a ball slides (kinetic friction opposing the contact-patch slip), then
 * rolls (rolling resistance, spin locked to travel), then spins down, then stops.
 * Vertical-axis (side) spin decays independently via spinning friction.
 */
import type { PhysicsConstants } from '../config';
import type { Vec3 } from '../types';
import type { Ball, BallMotionState } from './model';
import { integrateOrientation } from './vec';

/** Velocity of the ball's contact point relative to the cloth: u = v + R(k x w). */
export function slipVelocity(b: Ball<string>): Vec3 {
  // k x w = (-wy, wx, 0); contact point at -R k below centre -> u = v + R(-wy, wx, 0)
  return { x: b.vel.x - b.radius * b.angVel.y, y: b.vel.y + b.radius * b.angVel.x, z: 0 };
}

const SLIP_EPS = 1e-3; // m/s, below which sliding is treated as rolling/stopped

function decaySideSpin(b: Ball<string>, c: PhysicsConstants, dt: number): void {
  const rate = (5 * c.spinningFriction * c.gravity) / (2 * b.radius); // rad/s^2
  const dz = Math.sign(b.angVel.z) * rate * dt;
  b.angVel.z = Math.abs(dz) >= Math.abs(b.angVel.z) ? 0 : b.angVel.z - dz;
}

/**
 * Advance one ball by `dt`, mutating it in place. Returns the new motion state
 * (caller compares against the previous state to emit a transition event).
 */
export function stepBallMotion(b: Ball<string>, c: PhysicsConstants, dt: number): BallMotionState {
  const R = b.radius;

  // ---- airborne: ballistic flight under gravity, no cloth contact ----
  if (b.pos.z > R + 1e-4 || Math.abs(b.vel.z) > 1e-4) {
    const vzBefore = b.vel.z;
    b.vel.z -= c.gravity * dt;
    b.pos.x += b.vel.x * dt; // horizontal velocity is unchanged in the air
    b.pos.y += b.vel.y * dt;
    b.pos.z += 0.5 * (vzBefore + b.vel.z) * dt;
    if (b.pos.z <= R) {
      b.pos.z = R;
      b.vel.z = b.vel.z < 0 ? -b.vel.z * 0.35 : 0; // small bounce off the slate
      if (Math.abs(b.vel.z) < 0.15) b.vel.z = 0; // settle onto the cloth
    }
    b.orientation = integrateOrientation(b.orientation, b.angVel, dt);
    b.state = b.pos.z > R + 1e-4 ? 'airborne' : 'sliding';
    return b.state;
  }

  const vBeforeX = b.vel.x;
  const vBeforeY = b.vel.y;
  const u = slipVelocity(b);
  const uMag = Math.hypot(u.x, u.y);
  const speed = Math.hypot(b.vel.x, b.vel.y);

  // Slip decays at (7/2) mu_s g; if it would cross zero within this step, snap to
  // rolling instead of overshooting (which otherwise jitters and never settles).
  const rollThreshold = Math.max(SLIP_EPS, 3.5 * c.slidingFriction * c.gravity * dt);

  if (uMag > rollThreshold) {
    // ---- sliding: kinetic friction opposes the slip ----
    const ux = u.x / uMag;
    const uy = u.y / uMag;
    const aLin = c.slidingFriction * c.gravity * dt;
    b.vel.x -= aLin * ux;
    b.vel.y -= aLin * uy;
    // torque from friction: dw_xy = (5 mu_s g)/(2R) * (k x u_hat) = coef * (-uy, ux)
    const aAng = (5 * c.slidingFriction * c.gravity) / (2 * R) * dt;
    b.angVel.x += aAng * -uy;
    b.angVel.y += aAng * ux;
    decaySideSpin(b, c, dt);
    b.state = 'sliding';
  } else if (speed > c.linearSettle) {
    // ---- rolling: rolling resistance along -v_hat; spin locked to travel ----
    const vx = b.vel.x / speed;
    const vy = b.vel.y / speed;
    const newSpeed = Math.max(0, speed - c.rollingFriction * c.gravity * dt);
    b.vel.x = vx * newSpeed;
    b.vel.y = vy * newSpeed;
    // rolling constraint: w_xy = (k x v)/R = (-vy, vx, 0)/R
    b.angVel.x = -b.vel.y / R;
    b.angVel.y = b.vel.x / R;
    decaySideSpin(b, c, dt);
    b.state = newSpeed > c.linearSettle ? 'rolling' : Math.abs(b.angVel.z) > c.angularSettle ? 'spinning' : 'stationary';
  } else {
    // ---- (near) stationary: only residual side spin may remain ----
    b.vel.x = 0;
    b.vel.y = 0;
    b.angVel.x = 0;
    b.angVel.y = 0;
    if (Math.abs(b.angVel.z) > c.angularSettle) {
      decaySideSpin(b, c, dt);
      b.state = 'spinning';
    } else {
      b.angVel.z = 0;
      b.state = 'stationary';
    }
  }

  // integrate position (trapezoidal: average of pre/post-step velocity)
  b.pos.x += 0.5 * (vBeforeX + b.vel.x) * dt;
  b.pos.y += 0.5 * (vBeforeY + b.vel.y) * dt;

  b.orientation = integrateOrientation(b.orientation, b.angVel, dt);
  return b.state;
}
